import { useToast } from '@chakra-ui/react';
import type { SingleValue } from 'chakra-react-select';
import LZString from 'lz-string';
import { useSearchParams } from 'react-router-dom';

import { useCurrentRoleTaxonomyVersion } from '../../../../shared/selection-lists/hooks/useRoleTaxonomyAdapter';
import { useTabContentConfig } from '../../components/PageConfigProvider/PageConfigProvider';
import type { UserSavedFilterSet } from '../../components/SavedSets/useGetStoredSets';
import { createDefaultFilterMap } from '../contexts/defaultFilters';
import { useFilterContext } from '../contexts/FilterProvider';
import {
  generatePageKey,
  NO_PRIMARY_FILTER_KEY,
} from '../engine/filterGroupConsistency';
import {
  type Filter,
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  type Option,
  type PrimaryFilterId,
} from '../types/filters';
import type { TaxonomyConfig } from '../types/taxonomy';
import { isValidId } from '../utils/validate-parsed-filters';
import { validateTaxonomyFilters } from '../utils/validateTaxonomyFilters';
import { useCurrentSkillTaxonomyVersion } from './useCurrentSkillTaxonomyVersion';
import { useTaxonomyVersionToast } from './useTaxonomyVersionToast';

const isPrimaryFilterId = (id: string | undefined): id is PrimaryFilterId =>
  id === FilterId.COMPANY || id === FilterId.GEOGRAPHY || id === FilterId.ROLE;

const primaryFilterHasNoSelections = (
  filterMap: FilterSelectionsMap,
  primaryFilterId: PrimaryFilterId
): boolean => {
  const filter = filterMap.get(primaryFilterId);
  if (!filter || filter.type !== FilterType.TREE) return false;
  const primaryCount = filter.primarySelections?.length ?? 0;
  const selectionCount = filter.selections?.length ?? 0;
  return primaryCount === 0 || selectionCount === 0;
};

const validateTreeFilterIds = (
  filter: Filter,
  toast: ReturnType<typeof useToast>
) => {
  if (filter.type !== FilterType.TREE) return filter;

  const validPrimarySelections =
    filter.primarySelections?.filter((s) => isValidId(s.id)) || [];
  const validSelections =
    filter.selections?.filter((s) => isValidId(s.id)) || [];
  const primarySelections = filter.primarySelections || [];
  const selections = filter.selections || [];
  const hasInvalidSelections =
    (primarySelections.length > 0 || selections.length > 0) &&
    (validPrimarySelections.length !== primarySelections.length ||
      validSelections.length !== selections.length);

  if (hasInvalidSelections) {
    toast({
      title: `Your ${filter.name} filters have been removed from your saved filter set. Please re-select them.`,
      status: 'warning',
      duration: 6000,
      isClosable: true,
      position: 'top-right',
      id: 'saved-filter-set-removed',
    });
  }
  return {
    ...filter,
    primarySelections:
      filter.primarySelections?.filter((s) => isValidId(s.id)) || [],
    selections: filter.selections?.filter((s) => isValidId(s.id)) || [],
  };
};

export const useApplySavedFilterSet = () => {
  const { config, revelioPageId, primaryFilter } = useTabContentConfig();
  const { dispatch } = useFilterContext();
  const { version: roleTaxonomyVersion } = useCurrentRoleTaxonomyVersion();
  const { version: skillTaxonomyVersion } = useCurrentSkillTaxonomyVersion();
  const toast = useToast();
  const showTaxonomyVersionToast = useTaxonomyVersionToast();
  const [, setSearchParams] = useSearchParams();

  return async (value: SingleValue<Option<UserSavedFilterSet>> | undefined) => {
    if (!value) return;
    const filterUrl = value.entity?.filters;
    if (!filterUrl) return;

    try {
      const query = filterUrl.split('?')[1];
      if (!query) return;
      const params = new URLSearchParams(query);
      setSearchParams(params, { replace: true });

      const compressed = params.get('filters');
      if (!compressed) return;

      const decompressed =
        LZString.decompressFromEncodedURIComponent(compressed);
      if (!decompressed) return;
      const parsedRaw: [FilterId, Filter][] = JSON.parse(decompressed);

      const taxonomyValidatedFilters = validateTaxonomyFilters({
        filters: parsedRaw,
        taxonomyConfig: {
          roleTaxonomyVersion,
          skillTaxonomyVersion,
        } as TaxonomyConfig,
        showTaxonomyVersionToast,
      });

      // // Validate filter IDs before creating the Map
      const validatedFilterEntries: [FilterId, Filter][] =
        taxonomyValidatedFilters.map(
          ([filterId, filter]: [FilterId, Filter]) => {
            const validatedFilter = validateTreeFilterIds(filter, toast);
            return [filterId, validatedFilter];
          }
        );

      const filterMap = (() => {
        const map = new Map(validatedFilterEntries) as FilterSelectionsMap;
        if (
          !primaryFilter ||
          !isPrimaryFilterId(primaryFilter.id) ||
          !primaryFilterHasNoSelections(map, primaryFilter.id)
        ) {
          return map;
        }
        const defaultMap = createDefaultFilterMap(primaryFilter.id, {
          roleTaxonomyVersion,
          skillTaxonomyVersion,
        });
        const defaultPrimaryFilter = defaultMap.get(primaryFilter.id);
        if (!defaultPrimaryFilter) return map;
        return new Map([...map, [primaryFilter.id, defaultPrimaryFilter]]);
      })();

      const pageKey = generatePageKey(
        primaryFilter?.id ?? NO_PRIMARY_FILTER_KEY,
        revelioPageId ?? config.id
      );
      const defaultActiveCount = primaryFilter?.selectionDefault ?? 1;

      dispatch({
        type: 'REPLACE_FILTERS',
        payload: {
          filterMap,
          pageKey,
          defaultActiveCount,
          skipReconciliation: true,
        },
      });
    } catch (error) {
      console.error('Failed to apply saved filter set', error);
    }
  };
};
